'use client';

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { AppBar } from '@/components/ui/AppBar';

export interface ModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onOpenChange?: (open: boolean) => void;
  /** Modal content */
  children: React.ReactNode;
  /** Optional title shown in the header */
  label?: string;
  /** Optional subtitle rendered inside the app-bar header, below `label`. */
  subtitle?: string;
  /** Optional leading slot or back handler for the app bar */
  leading?: React.ReactNode;
  /** Optional trailing slot or close handler for the app bar */
  trailing?: React.ReactNode;
  /** Click handler to show a default back icon button on the left */
  onBack?: () => void;
  /** Click handler for close button */
  onClose?: () => void;
  /** Whether the modal can be dismissed by clicking overlay or pressing ESC */
  dismissible?: boolean;
  /** Custom class names */
  overlayClassName?: string;
  contentClassName?: string;
  bodyClassName?: string;
  /** Maximum width of the modal */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Whether to close the modal when the overlay is clicked */
  closeOnOverlayClick?: boolean;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
};

export function Modal({
  open,
  onOpenChange,
  children,
  label,
  subtitle,
  leading,
  trailing,
  onBack,
  onClose,
  dismissible = true,
  overlayClassName,
  contentClassName,
  bodyClassName,
  maxWidth = 'md',
  closeOnOverlayClick = true,
}: ModalProps) {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Handle ESC key
  useEffect(() => {
    if (!open || !dismissible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange?.(false);
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, dismissible, onOpenChange, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (!closeOnOverlayClick) return;
    if (dismissible && e.target === e.currentTarget) {
      onOpenChange?.(false);
      onClose?.();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (!closeOnOverlayClick) return;
    if (dismissible) {
      onOpenChange?.(false);
      onClose?.();
    }
  };

  const modalContent = (
    <div
      className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', overlayClassName)}
      onClick={handleOverlayClick}
    >
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" onClick={handleBackdropClick} />

      {/* Modal Content */}
      <div
        className={cn(
          'relative z-10 w-full',
          'flex flex-col',
          'rounded-2xl border border-secondary bg-primary shadow-lg',
          'max-h-[calc(100dvh-2rem)]',
          maxWidthClasses[maxWidth],
          contentClassName,
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={label ? 'modal-title' : undefined}
        onClick={e => e.stopPropagation()}
      >
        {/* Header — render the AppBar (which includes the close X)
            whenever we have a title OR a close/back handler. Previously
            gated on `label` alone, which silently dropped the X for
            title-less modals like the report prompt. */}
        {(label || onClose || onBack || leading || trailing) && (
          <AppBar
            title={label ?? ''}
            {...(subtitle ? { subtitle } : {})}
            leading={leading}
            trailing={trailing}
            {...(onBack ? { onBack } : {})}
            {...(onClose ? { onClose } : {})}
          />
        )}

        {/* Body */}
        <div className={cn('flex-1 min-h-0 overflow-y-auto', 'px-6 py-4', bodyClassName)}>
          {children}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null;
}

export default Modal;
